import React, { useState } from 'react';
import { AppBar, Toolbar, Typography, Button, Box, IconButton } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { FaInfoCircle, FaShoppingCart, FaMapMarkerAlt, FaBars, FaTimes, FaFacebook, FaInstagram, FaTiktok, FaWhatsapp, FaFileAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const Header = ({ onOpenMenu }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const navItems = [
    { text: 'À propos', icon: <FaInfoCircle /> },
    { text: 'Nos produits', icon: <FaShoppingCart /> },
    { text: 'Localisation', icon: <FaMapMarkerAlt /> },
  ];

  return (
    <AppBar component="header" position="fixed" sx={{ 
      backgroundColor: '#FEF3DE', 
      boxShadow: '0 4px 10px rgba(139, 69, 19, 0.15)',
    }}>
      <Toolbar sx={{ 
        justifyContent: 'space-between', 
        maxWidth: '1200px', 
        width: '100%', 
        margin: 'auto', 
        padding: '10px 5px'
      }}>
        {/* Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <motion.img
                src="/images/soleil.webp"
                alt="Logo Pain du Soleil"
                loading="lazy"
                width="35"
                height="35"
                style={{ marginRight: '12px' }}
                initial={{ rotate: 0 }}
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
              />
              <Typography
                variant="h6"
                sx={{
                  color: '#8B4513',
                  fontWeight: 'bold',
                  fontSize: { xs: '1.1rem', md: '1.4rem' },
                  fontFamily: "'Playfair Display', serif"
                }}
              >
                Pain du Soleil
              </Typography>
            </Box>
          </Link>
        </Box>

        {/* Navigation desktop */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
          {navItems.map((item, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              style={{ display: 'inline-block' }}
            >
              <Button
                color="inherit"
                href={`/${item.text.toLowerCase().replace(' ', '-')}`}
                sx={{
                  color: '#5D4037',
                  fontWeight: '600',
                  mx: 1.5,
                  py: 1,
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  transition: 'all 0.3s ease',
                  '&:hover': { 
                    backgroundColor: 'rgba(139, 69, 19, 0.08)',
                    color: '#8B4513'
                  },
                }}
                startIcon={item.icon}
              >
                {item.text}
              </Button>
            </motion.div>
          ))}
          <Link to="/devis" style={{ textDecoration: 'none' }}>
            <DevisButton />
          </Link>
          <Link to="/contact" style={{ textDecoration: 'none' }}>
            <ContactButton />
          </Link>
        </Box>

        {/* Menu hamburger for mobile */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, marginRight: '4px' }}>
          <IconButton 
            onClick={toggleMenu} 
            sx={{ 
              color: '#8B4513',
              transition: 'all 0.3s ease'
            }}
            aria-label="Ouvrir le menu de navigation"
          >
            <motion.div animate={{ rotate: isMenuOpen ? 180 : 0 }} transition={{ duration: 0.5 }}>
              {isMenuOpen ? <FaTimes size={22} /> : <FaBars size={22} />}
            </motion.div>
          </IconButton>
        </Box>
      </Toolbar>

      {/* Mobile fullscreen menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              zIndex: 1000,
              backgroundColor: '#FEF3DE',
              backgroundImage: 'linear-gradient(135deg, #FEF3DE 0%, #FCE4C0 100%)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Box sx={{ position: 'absolute', top: '16px', right: '24px' }}>
              <IconButton 
                onClick={toggleMenu} 
                sx={{ color: '#8B4513' }} 
                aria-label="Fermer le menu"
              >
                <FaTimes size={26} />
              </IconButton>
            </Box>

            <Box sx={{ position: 'absolute', top: '20px', left: '50%', transform: 'translateX(-50%)' }}>
              <img 
                src="/images/logo.webp" 
                alt="Logo Pain du Soleil" 
                loading="lazy" 
                width="90" 
                height="90" 
                style={{ width: '90px' }} 
              />
            </Box>

            <Box sx={{ width: '85%', maxWidth: '320px', marginTop: '30px' }}>
              {navItems.map((item, index) => (
                <Link
                  key={index}
                  to={`/${item.text.toLowerCase().replace(' ', '-')}`}
                  style={{ textDecoration: 'none', width: '100%', textAlign: 'center' }}
                  onClick={toggleMenu}
                >
                  <motion.div
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <Button
                      color="inherit"
                      sx={{
                        color: '#5D4037',
                        fontWeight: '600',
                        my: 1.2,
                        width: '100%',
                        padding: '14px 0',
                        textTransform: 'none',
                        fontSize: '1.15rem',
                        '&:hover': { backgroundColor: 'rgba(139, 69, 19, 0.08)', color: '#8B4513' },
                      }}
                      startIcon={item.icon}
                    >
                      {item.text}
                    </Button>
                  </motion.div>
                </Link>
              ))}

              <Box sx={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '24px' }}>
                <Link to="/devis" style={{ textDecoration: 'none', width: '48%' }} onClick={toggleMenu}>
                  <DevisButton />
                </Link>
                <Link to="/contact" style={{ textDecoration: 'none', width: '48%' }} onClick={toggleMenu}>
                  <ContactButton mobile />
                </Link>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: '30px', marginTop: '60px' }}>
              <motion.div whileHover={{ scale: 1.1, rotate: 10 }} whileTap={{ scale: 0.9 }}>
                <Link to="https://www.facebook.com/share/16Bzdpqc7N/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" style={{ color: '#8B4513' }}>
                  <FaFacebook size={24} />
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.1, rotate: 10 }} whileTap={{ scale: 0.9 }}>
                <Link to="https://www.instagram.com/traiteur_du_soleil_" target="_blank" rel="noopener noreferrer" style={{ color: '#8B4513' }}>
                  <FaInstagram size={24} />
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.1, rotate: 10 }} whileTap={{ scale: 0.9 }}>
                <Link to="https://www.tiktok.com/@traiteur_du_soleil?_t=ZN-8vRwmdPOzoM&_r=1" target="_blank" rel="noopener noreferrer" style={{ color: '#8B4513' }}>
                  <FaTiktok size={24} />
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.1, rotate: 10 }} whileTap={{ scale: 0.9 }}>
                <Link to="https://wa.me/+33785853636" target="_blank" rel="noopener noreferrer" style={{ color: '#8B4513' }}>
                  <FaWhatsapp size={24} />
                </Link>
              </motion.div>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </AppBar>
  );
};

// Stylé button with warmth and character
const ContactButton = ({ mobile }) => {
  return (
    <StyledContactWrapper>
      <button aria-label="Contacter Pain du Soleil">
        <div className="svg-wrapper-1">
          <div className="svg-wrapper">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={20} height={20}>
              <path fill="none" d="M0 0h24v24H0z" />
              <path fill="currentColor" d="M1.946 9.315c-.522-.174-.527-.455.01-.634l19.087-6.362c.529-.176.832.12.684.638l-5.454 19.086c-.15.529-.455.547-.679.045L12 14l6-8-8 6-8.054-2.685z" />
            </svg>
          </div>
        </div>
        <span>Nous contacter</span>
      </button>
    </StyledContactWrapper>
  );
};

// Devis button with different style
const DevisButton = () => {
  return (
    <StyledDevisWrapper>
      <button aria-label="Demander un devis">
        <div className="svg-wrapper-1">
          <div className="svg-wrapper">
            <FaFileAlt size={18} />
          </div>
        </div>
        <span>Demander un devis</span>
      </button>
    </StyledDevisWrapper>
  );
};

const StyledContactWrapper = styled.div`
  button {
    font-family: 'Roboto, sans-serif';
    font-size: 15px;
    background: #8B4513;
    color: white;
    padding: 0.5em 1.2em;
    display: flex;
    align-items: center;
    border: none;
    border-radius: 28px;
    overflow: hidden;
    transition: all 0.3s ease;
    cursor: pointer;
    margin-left: 12px;
    font-weight: 600;
    box-shadow: 0 4px 8px rgba(139, 69, 19, 0.25);
    
    @media (max-width: 600px) { 
      margin: 0 auto; 
      display: inline-flex;
      width: 100%;
      justify-content: center;
      padding: 0.65em 0.8em;
      height: 48px;
      min-height: 48px;
    }
    
    &:hover {
      background: #A0522D;
      transform: translateY(-2px);
      box-shadow: 0 6px 12px rgba(139, 69, 19, 0.3);
    }
    
    &:active {
      transform: translateY(0);
      box-shadow: 0 2px 4px rgba(139, 69, 19, 0.2);
    }
  }
  
  button span { 
    display: block; 
    margin-left: 0.4em; 
    transition: all 0.3s ease-in-out; 
  }
  
  button svg { 
    display: block; 
    transition: transform 0.3s ease-in-out; 
  }
  
  button:hover .svg-wrapper { 
    animation: fly-1 0.6s ease-in-out infinite alternate;
  }
  
  button:hover svg { 
    transform: translateX(3px) rotate(5deg); 
  }
  
  @keyframes fly-1 {
    from {
      transform: translateY(0.1em);
    }
    to {
      transform: translateY(-0.1em);
    }
  }
`;

const StyledDevisWrapper = styled.div`
  button {
    font-family: 'Roboto, sans-serif';
    font-size: 15px;
    background: #3A7D44; /* Différente couleur pour distinguer de Contact */
    color: white;
    padding: 0.5em 1.2em;
    display: flex;
    align-items: center;
    border: none;
    border-radius: 28px;
    overflow: hidden;
    transition: all 0.3s ease;
    cursor: pointer;
    margin-left: 12px;
    font-weight: 600;
    box-shadow: 0 4px 8px rgba(58, 125, 68, 0.25);
    
    @media (max-width: 600px) { 
      margin: 0 auto; 
      display: inline-flex;
      width: 100%;
      justify-content: center;
      padding: 0.65em 0.8em;
      height: 48px;
      min-height: 48px;
    }
    
    &:hover {
      background: #2E6437;
      transform: translateY(-2px);
      box-shadow: 0 6px 12px rgba(58, 125, 68, 0.3);
    }
    
    &:active {
      transform: translateY(0);
      box-shadow: 0 2px 4px rgba(58, 125, 68, 0.2);
    }
  }
  
  button span { 
    display: block; 
    margin-left: 0.4em; 
    transition: all 0.3s ease-in-out; 
  }
  
  button svg { 
    display: block; 
    transition: transform 0.3s ease-in-out; 
  }
  
  button:hover .svg-wrapper { 
    animation: bounce 0.6s ease-in-out infinite alternate;
  }
  
  button:hover svg { 
    transform: scale(1.1); 
  }
  
  @keyframes bounce {
    from {
      transform: scale(1);
    }
    to {
      transform: scale(1.15);
    }
  }
`;

export default Header;